/*
 * Axis helpers for the Insights charts. Display only: they choose which ticks
 * and labels to draw, never which data points exist.
 */

const INTEGER_STEPS = [1, 2, 3, 4, 5, 10, 15, 20, 25, 50, 100];

// Integer ticks from 0 for a count axis (workouts): at most `maxTicks`
// intervals, never a fractional label. Returns the ticks; the last is the
// axis maximum.
export function integerTicks(maxValue, maxTicks = 4) {
  const max = Math.max(1, Math.ceil(Number(maxValue) || 0));
  const raw = max / maxTicks;
  const step = INTEGER_STEPS.find((candidate) => candidate >= raw)
    || 10 ** Math.ceil(Math.log10(raw));
  const top = Math.ceil(max / step) * step;
  const ticks = [];
  for (let tick = 0; tick <= top; tick += step) ticks.push(tick);
  return ticks;
}

// Which of `count` evenly spaced columns get an x-axis label. Every column is
// still plotted. Labels share one step, anchored on the most recent column, so
// spacing is even, the latest week is always named and no two labels crowd.
export function axisLabelIndexes(count, maxLabels) {
  if (count <= 0) return [];
  const limit = Math.max(1, Math.floor(maxLabels));
  if (count <= limit) return [...Array(count).keys()];
  const step = Math.ceil((count - 1) / Math.max(1, limit - 1));
  const indexes = [];
  for (let index = count - 1; index >= 0; index -= step) indexes.unshift(index);
  return indexes;
}

// How many labels fit across `width` px for labels up to `longestLabel`
// characters (0.625rem labels, ~5.5px per character plus a gap), capped so a
// phone shows 4–5 dates at most.
export function maxAxisLabels(width, longestLabel, cap = 5) {
  const labelWidth = Math.max(1, longestLabel) * 5.5 + 12;
  return Math.max(2, Math.min(cap, Math.floor(width / labelWidth)));
}

// Cycle timelines longer than this many weeks (two 6-week cycles and up)
// label weeks with their bare number under the cycle names, in every chart,
// so "W1 W2 …" never runs together on a phone.
export const COMPACT_WEEK_LABELS_AFTER = 10;

const RANGE_STEP_FACTORS = [1, 2, 2.5, 5];

// Ticks for a value axis that is not anchored at 0 (loads on the series
// scale): at most `maxTicks` intervals of a 1 / 2 / 2.5 / 5 × 10^k step,
// covering [min, max]. A flat series gets one step either side.
export function rangeTicks(min, max, maxTicks = 4) {
  let low = Number(min);
  let high = Number(max);
  if (!Number.isFinite(low) || !Number.isFinite(high)) return [0, 1];
  if (high < low) [low, high] = [high, low];
  if (high === low) {
    const pad = Math.max(1, Math.abs(high) * 0.05);
    low -= pad;
    high += pad;
  }
  const raw = (high - low) / Math.max(1, maxTicks);
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = RANGE_STEP_FACTORS.map((factor) => factor * magnitude).find((candidate) => candidate >= raw) || 10 * magnitude;
  const first = Math.floor(low / step) * step;
  const last = Math.ceil(high / step) * step;
  const ticks = [];
  for (let tick = first; tick <= last + step / 2; tick += step) ticks.push(Math.round(tick * 1000) / 1000);
  return ticks;
}
